import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("auth.passwordsNoMatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(t("auth.passwordUpdatedToast"));
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50 px-5 py-28">
      <Seo noindex title="Reset Password | Belo Rentals" path="/reset-password" />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-card p-8">
          {!token ? (
            <div className="text-center">
              <h1 className="font-heading text-2xl font-semibold text-slate-900">{t("auth.invalidLink")}</h1>
              <p className="text-slate-500 text-sm mt-3">{t("auth.invalidLinkSub")}</p>
              <Link to="/forgot-password" className="inline-block mt-6 text-blue-600 font-semibold hover:underline">{t("auth.requestNew")}</Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done" className="text-center">
              <span className="grid place-items-center w-14 h-14 rounded-full bg-green-100 text-green-600 mx-auto"><CheckCircle2 size={28} /></span>
              <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-5">{t("auth.passwordUpdated")}</h1>
              <p className="text-slate-500 text-sm mt-3">{t("auth.redirectingSignIn")}</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="reset-form">
              <div className="flex items-center gap-3 text-slate-900 mb-2">
                <span className="grid place-items-center w-9 h-9 rounded-lg bg-blue-50 text-blue-600"><Lock size={18} /></span>
                <h1 className="font-heading text-2xl font-semibold">{t("auth.setNewPassword")}</h1>
              </div>
              <p className="text-slate-500 text-sm mb-7">{t("auth.setNewSub")}</p>
              {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl mb-6">{error}</div>}
              <div className="space-y-5">
                <div>
                  <span className="label-caps text-slate-400 block mb-2">{t("auth.newPassword")}</span>
                  <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="belo-input" placeholder={t("auth.passwordMin")} />
                </div>
                <div>
                  <span className="label-caps text-slate-400 block mb-2">{t("auth.confirmPassword")}</span>
                  <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="belo-input" placeholder={t("auth.reenterPassword")} />
                </div>
              </div>
              <button type="submit" disabled={loading} data-testid="reset-submit" className="mt-7 w-full bg-blue-600 text-white py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
                {loading ? t("auth.updating") : t("auth.updatePassword")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
